// Compat shim — Phase 2 PR1 of the adaptive-lookup-field refactor.
// See `docs/audits/adaptive-field-refactor-proposal.md` §3.2.
//
// The multi-select lookup component's implementation now lives in
// `AdaptiveLookupField`. This wrapper keeps the historical `LookupMultiSelect`
// API alive so the ~11 admin multi-select call sites (ReferenceEditor,
// LookupValueEditor's dial-styles properties field, BrandPortal panels,
// etc.) don't have to migrate at once — PR2 does the mechanical import
// rename. External-repo consumers are unaffected.
//
// The shim maps `values` → `multiValues` and `onChange` → `onMultiChange` and
// forwards everything else. If a call site's collection is flipped to single
// in Firestore without swapping to `LookupFieldSelect`, AdaptiveLookupField
// dev-warns in the console and keeps rendering the multi branch — the bound
// shape wins over a drifted flag, so the field stays usable. See the
// `effectiveMultiSelect` note in AdaptiveLookupField.

import { AdaptiveLookupField } from './AdaptiveLookupField';

interface LookupMultiSelectProps {
  label: string;
  values: string[];
  lookupCollection: string;
  onChange: (values: string[]) => void;
  disabled?: boolean;
  // When false, hide the add-custom-value path (custom input + CustomValueActions)
  // and show a disabled "Request new value" affordance. Defaults to true to
  // preserve super-admin behavior.
  allowCustom?: boolean;
  // Brand Portal Phase 2.5: when provided (and allowCustom===false), the
  // "Request new value →" affordance becomes a live button. The parent owns the
  // request modal.
  onRequestNewValue?: (seed: { query?: string }) => void;
  // Show an inline "Request sent" confirmation next to the affordance.
  requestSent?: boolean;
}

export function LookupMultiSelect({
  label,
  values,
  lookupCollection,
  onChange,
  disabled,
  allowCustom,
  onRequestNewValue,
  requestSent,
}: LookupMultiSelectProps) {
  return (
    <AdaptiveLookupField
      label={label}
      lookupCollection={lookupCollection}
      multiValues={values}
      onMultiChange={onChange}
      disabled={disabled}
      allowCustom={allowCustom}
      onRequestNewValue={onRequestNewValue}
      requestSent={requestSent}
    />
  );
}
